// Coda delle letture: ogni chiamata a speak() si accoda invece di sovrapporsi o
// interrompere quella in corso — una lettura parte solo quando la precedente è
// davvero finita (onEnd), mai in parallelo.
let _queue = [];
let _speakingJob = false;

export function cancelSpeech() {
  _queue = [];
  _speakingJob = false;
  if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
}

function pickVoice(lang, name) {
  const vs = window.speechSynthesis?.getVoices?.() || [];
  if (name) {
    const byName = vs.find((v) => v.name === name);
    if (byName) return byName;
  }
  return (
    vs.find((v) => v.lang === lang) ||
    vs.find((v) => v.lang && v.lang.startsWith((lang || "it").slice(0, 2)))
  );
}

let _voicesReady = null;
// Chrome (e alcuni WebView) restituisce getVoices() vuoto finché l'evento "voiceschanged"
// non è sparato una prima volta: speak() chiamato prima d'allora spesso fallisce in
// silenzio (nessun suono, nessun errore). Aspettiamo le voci una volta sola (con un
// timeout di sicurezza, per non restare bloccati sui browser che l'evento non lo sparano).
function waitForVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return Promise.resolve();
  if (window.speechSynthesis.getVoices().length > 0) return Promise.resolve();
  if (_voicesReady) return _voicesReady;
  _voicesReady = new Promise((resolve) => {
    const done = () => resolve();
    window.speechSynthesis.addEventListener("voiceschanged", done, { once: true });
    setTimeout(done, 1000);
  });
  return _voicesReady;
}

// Speak one or more texts sequentially using the Web Speech API.
// A watchdog guarantees onEnd always fires (even without available voices / headless).
function speakSequenceNow(texts, { lang = "it-IT", voiceName, onStart, onEnd } = {}) {
  const list = (Array.isArray(texts) ? texts : [texts]).filter((t) => t && String(t).trim());

  let finished = false;
  const finishAll = () => {
    if (finished) return;
    finished = true;
    onEnd && onEnd();
  };

  if (typeof window === "undefined" || !window.speechSynthesis || list.length === 0) {
    onStart && onStart();
    finishAll();
    return;
  }

  onStart && onStart();

  waitForVoices().then(() => {
    if (finished) return; // cancelSpeech() chiamato mentre aspettavamo le voci
    window.speechSynthesis.cancel();

    const totalChars = list.reduce((n, t) => n + String(t).length, 0);
    const watchdogMs = Math.min(20000, Math.max(2500, totalChars * 90));
    const watchdog = setTimeout(finishAll, watchdogMs);

    let done = 0;
    const step = () => {
      done += 1;
      if (done >= list.length) {
        clearTimeout(watchdog);
        finishAll();
      }
    };

    list.forEach((text) => {
      const u = new SpeechSynthesisUtterance(String(text));
      u.lang = lang;
      const v = pickVoice(lang, voiceName);
      if (v) u.voice = v;
      u.rate = 1;
      u.pitch = 1;
      u.onend = step;
      u.onerror = step;
      window.speechSynthesis.speak(u);
    });
  });
}

async function speakNow(texts, { lang = "it-IT", voiceName, onStart, onEnd } = {}) {
  speakSequenceNow(texts, { lang, voiceName, onStart, onEnd });
}

// Pesca il prossimo job in coda e lo riproduce, ma solo se non ne è già attivo uno:
// ogni job passa il proprio onEnd "avvolto" così l'avvio del successivo è agganciato
// alla vera fine del precedente, non alla chiamata di speak().
function pumpQueue() {
  if (_speakingJob) return;
  const job = _queue.shift();
  if (!job) return;
  _speakingJob = true;
  const { texts, opts } = job;
  const onEnd = () => {
    _speakingJob = false;
    opts.onEnd && opts.onEnd();
    pumpQueue();
  };
  speakNow(texts, { ...opts, onEnd });
}

// Stessa firma/comportamento pubblico di prima, ma ora ogni chiamata si accoda invece
// di sovrapporsi (audio cloud) o interrompere (motore browser) una lettura in corso.
export function speak(texts, opts = {}) {
  _queue.push({ texts, opts });
  pumpQueue();
}

// Web Speech API speech-to-text. Returns the recognition instance (or null if unsupported).
export function startRecognition(lang, { onResult, onEnd } = {}) {
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) {
    onEnd && onEnd("unsupported");
    return null;
  }
  const rec = new SR();
  rec.lang = lang || "it-IT";
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.onresult = (e) => {
    const t = e.results?.[0]?.[0]?.transcript || "";
    onResult && onResult(t);
  };
  rec.onerror = () => onEnd && onEnd("error");
  rec.onend = () => onEnd && onEnd();
  try {
    rec.start();
  } catch {
    onEnd && onEnd("error");
    return null;
  }
  return rec;
}

